import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import {
  Close,
  Delete,
  GetApp,
  MailOutline,
  NavigateBefore,
  NavigateNext,
  PhotoCamera,
  PlayCircleOutline,
  Flag,
  ZoomIn,
} from "@material-ui/icons";

import SessionStatusChip from "./sessionStatusChip";
import {
  formatDateTime,
  formatDistance,
  getEmail,
  getRatio,
  getSessionEndedAt,
  getSessionPhotos,
  getTeamName,
  hasValue,
  isSessionLive,
} from "../sessionUtils";

const PLACEHOLDER = "—";

// Tuile de statistique : libellé sourd, valeur en avant. Une valeur pas encore
// remontée par le mobile s'affiche en tiret, jamais en 0 (qui serait un fait).
const StatTile = ({ classes, label, value, unit }) => {
  const empty = !hasValue(value);

  return (
    <div className={classes.statTile}>
      <Typography className={classes.statLabel}>{label}</Typography>
      <Typography
        component="p"
        className={`${classes.statValue} ${
          empty ? classes.statValuePending : ""
        }`}
      >
        {empty ? PLACEHOLDER : value}
        {!empty && unit ? (
          <span className={classes.statUnit}>{unit}</span>
        ) : null}
      </Typography>
    </div>
  );
};

// Jauge réalisé / total. Le total peut manquer (ancienne session) : on retombe
// alors sur la valeur seule, sans barre trompeuse.
const Meter = ({ classes, label, done, total }) => {
  const ratio = getRatio(done, total);

  return (
    <div className={classes.meterRow}>
      <div className={classes.meterHead}>
        <span className={classes.meterLabel}>{label}</span>
        <span className={classes.meterValue}>
          {hasValue(done) ? done : PLACEHOLDER}
          {total ? ` / ${total}` : ""}
        </span>
      </div>
      {total ? (
        <div className={classes.meterTrack}>
          <div
            className={classes.meterFill}
            style={{ width: `${ratio * 100}%` }}
          />
        </div>
      ) : null}
    </div>
  );
};

const MetaItem = ({ classes, icon: Icon, label, value }) => (
  <div className={classes.detailMetaItem}>
    <Icon className={classes.detailMetaIcon} />
    <span>
      {label}&nbsp;
      <span className={classes.detailMetaValue}>{value || PLACEHOLDER}</span>
    </span>
  </div>
);

const SessionDetail = ({ session, classes, onDeletePhoto }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenUrl, setFullscreenUrl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const photos = getSessionPhotos(session, t);
  const photoCount = photos.length;

  // Le polling peut faire varier le nombre de photos pendant que le détail est
  // ouvert (arrivée en direct, suppression) : on garde l'index dans les bornes.
  useEffect(() => {
    if (currentIndex > 0 && currentIndex > photoCount - 1) {
      setCurrentIndex(Math.max(0, photoCount - 1));
    }
  }, [photoCount, currentIndex]);

  if (!session) return null;

  const currentPhoto = photos[Math.min(currentIndex, photoCount - 1)] || null;
  const live = isSessionLive(session);
  const endedAt = getSessionEndedAt(session);
  const distance = formatDistance(session.distanceTraveled);

  const goPrev = () =>
    setCurrentIndex((prev) => (prev === 0 ? photoCount - 1 : prev - 1));
  const goNext = () =>
    setCurrentIndex((prev) => (prev === photoCount - 1 ? 0 : prev + 1));

  const handleDownload = () => {
    if (!currentPhoto?.photoUrl) return;
    const link = document.createElement("a");
    link.href = currentPhoto.photoUrl;
    link.download = currentPhoto.photoUrl.split("/").pop() || "team-photo.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDelete = async () => {
    // On ferme d'abord la confirmation pour éviter les conflits de focus.
    setDeleteDialogOpen(false);
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    if (currentPhoto?.id && onDeletePhoto) {
      await onDeletePhoto(currentPhoto.id);
    }
  };

  return (
    <Box>
      {/* En-tête : qui joue, dans quel état, et quand */}
      <div className={classes.detailHeader}>
        <div className={classes.detailTitleRow}>
          <Typography component="h2" className={classes.detailTeamName}>
            {getTeamName(session, t)}
          </Typography>
          <SessionStatusChip session={session} classes={classes} />
        </div>
        <div className={classes.detailMeta}>
          <MetaItem
            classes={classes}
            icon={MailOutline}
            label={t("teamPhotos.detail.email")}
            value={getEmail(session)}
          />
          <MetaItem
            classes={classes}
            icon={PlayCircleOutline}
            label={t("teamPhotos.detail.started_at")}
            value={formatDateTime(session.created_at)}
          />
          <MetaItem
            classes={classes}
            icon={Flag}
            label={t("teamPhotos.detail.ended_at")}
            value={
              endedAt ? formatDateTime(endedAt) : t("teamPhotos.detail.ongoing")
            }
          />
          <MetaItem
            classes={classes}
            icon={PhotoCamera}
            label={t("teamPhotos.detail.photos")}
            value={`${photoCount}`}
          />
        </div>
      </div>

      <Grid container spacing={2}>
        {/* Photos de la session */}
        <Grid item xs={12} md={7}>
          <div className={classes.detailSection}>
            <div className={classes.photoToolbar}>
              <Typography className={classes.detailSectionTitle}>
                {t("teamPhotos.detail.photos_section")}
              </Typography>
              {photoCount > 1 && (
                <Box display="flex" alignItems="center">
                  <span className={classes.photoCounter}>
                    {currentIndex + 1} / {photoCount}
                  </span>
                  <IconButton
                    onClick={goPrev}
                    size="small"
                    title={t("teamPhotos.detail.previous_photo")}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    onClick={goNext}
                    size="small"
                    title={t("teamPhotos.detail.next_photo")}
                  >
                    <NavigateNext />
                  </IconButton>
                </Box>
              )}
            </div>

            {currentPhoto ? (
              <>
                <div className={classes.photoFrame}>
                  <img
                    src={currentPhoto.photoUrl}
                    alt={t("teamPhotos.carousel.team_photo")}
                    className={classes.photoImage}
                    onClick={() => setFullscreenUrl(currentPhoto.photoUrl)}
                  />
                  <IconButton
                    size="small"
                    className={`${classes.photoOverlayButton} ${classes.photoZoomButton}`}
                    onClick={() => setFullscreenUrl(currentPhoto.photoUrl)}
                    title={t("teamPhotos.carousel.photo_fullscreen")}
                  >
                    <ZoomIn fontSize="small" />
                  </IconButton>
                  {currentPhoto.id && (
                    <IconButton
                      size="small"
                      className={`${classes.photoOverlayButton} ${classes.photoDeleteButton}`}
                      onClick={() => setDeleteDialogOpen(true)}
                      title={t("teamPhotos.carousel.delete_photo")}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </div>

                <div className={classes.photoEnigma}>
                  <Typography className={classes.photoEnigmaTitle}>
                    {currentPhoto.enigmaName}
                  </Typography>
                  {currentPhoto.enigmaDescription && (
                    <Typography className={classes.photoEnigmaDescription}>
                      {currentPhoto.enigmaDescription}
                    </Typography>
                  )}
                </div>

                {photoCount > 1 && (
                  <div className={classes.thumbStrip}>
                    {photos.map((photo, index) => (
                      <img
                        key={photo.id || index}
                        src={photo.photoUrl}
                        alt={`${t("teamPhotos.carousel.photo")} ${index + 1}`}
                        className={`${classes.thumb} ${
                          index === currentIndex ? classes.thumbActive : ""
                        }`}
                        onClick={() => setCurrentIndex(index)}
                      />
                    ))}
                  </div>
                )}

                <Button
                  variant="contained"
                  startIcon={<GetApp />}
                  className={classes.downloadButton}
                  onClick={handleDownload}
                >
                  {t("teamPhotos.carousel.download_photo")}
                </Button>
              </>
            ) : (
              <div className={classes.emptyPhotos}>
                <PhotoCamera className={classes.emptyPhotosIcon} />
                <Typography variant="body2">
                  {live
                    ? t("teamPhotos.detail.no_photos_yet_live")
                    : t("teamPhotos.carousel.no_photos")}
                </Typography>
              </div>
            )}
          </div>
        </Grid>

        {/* Progression : le chiffre phare de la vue + les jauges réalisé/total */}
        <Grid item xs={12} md={5}>
          <div className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>
              {t("teamPhotos.detail.progress_section")}
            </Typography>

            <Typography className={classes.heroLabel}>
              {t("teamPhotos.carousel.points_won")}
            </Typography>
            <Typography component="p" className={classes.heroValue}>
              {hasValue(session.pointsWon) ? session.pointsWon : PLACEHOLDER}
              {session.totalPoints ? (
                <span className={classes.heroUnit}>
                  / {session.totalPoints} {t("teamPhotos.detail.points_unit")}
                </span>
              ) : null}
            </Typography>
            {session.totalPoints ? (
              <div className={classes.meterTrack} style={{ marginTop: 12 }}>
                <div
                  className={classes.meterFill}
                  style={{
                    width: `${
                      getRatio(session.pointsWon, session.totalPoints) * 100
                    }%`,
                  }}
                />
              </div>
            ) : null}

            <Meter
              classes={classes}
              label={t("teamPhotos.carousel.zones_succeeded")}
              done={session.zonesSucceded}
              total={session.totalZones}
            />
            <Meter
              classes={classes}
              label={t("teamPhotos.carousel.enigmas_succeeded")}
              done={session.enigmasSucceded}
              total={session.totalEnigmas}
            />
          </div>
        </Grid>

        {/* Statistiques détaillées */}
        <Grid item xs={12}>
          <div className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>
              {t("teamPhotos.carousel.game_statistics")}
            </Typography>
            <div className={classes.statGrid}>
              <StatTile
                classes={classes}
                label={t("teamPhotos.detail.score")}
                value={session.score}
              />
              <StatTile
                classes={classes}
                label={t("teamPhotos.carousel.total_time")}
                value={session.totalTime}
                unit={t("teamPhotos.carousel.minutes")}
              />
              <StatTile
                classes={classes}
                label={t("teamPhotos.detail.distance_traveled")}
                value={distance?.value}
                unit={distance?.unit}
              />
              <StatTile
                classes={classes}
                label={t("teamPhotos.detail.clues_used")}
                value={session.clueUsed}
              />
              <StatTile
                classes={classes}
                label={t("teamPhotos.carousel.zones_failed")}
                value={session.zonesFailed}
              />
              <StatTile
                classes={classes}
                label={t("teamPhotos.carousel.enigmas_failed")}
                value={session.enigmasFailed}
              />
            </div>
          </div>
        </Grid>
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-photo-dialog-title"
        disableRestoreFocus
      >
        <DialogTitle id="delete-photo-dialog-title">
          {t("teamPhotos.carousel.delete_photo_confirm")}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="default">
            {t("teamPhotos.carousel.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
          >
            {t("teamPhotos.carousel.confirm_delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!fullscreenUrl}
        onClose={() => setFullscreenUrl(null)}
        maxWidth="lg"
        fullWidth
        className="photo-modal"
        disableRestoreFocus
      >
        <DialogTitle className="photo-modal-title">
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <IconButton
              onClick={() => setFullscreenUrl(null)}
              className="close-button"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className="photo-modal-content">
          {fullscreenUrl && (
            <img
              src={fullscreenUrl}
              alt={t("teamPhotos.carousel.team_photo")}
              className="fullscreen-photo"
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SessionDetail;
